import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Link, useLocation } from "react-router-dom";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import { crestWall } from "./siteData";
import { crestFallback } from "./SiteCards";
import { dashboardPath, signedIn } from "./session";
import { useHashScroll, usePageMeta, useSiteMotion } from "./useSiteMotion";
import { loadListings } from "../Components/EventFeed/EventFeed";
import { buildEventFeed } from "../helper/eventFeed";
import favIcon from "../Assets/favicon.ico";
import founder from "../Assets/founder-dancers.jpg";
import "./site.css";

// /organisers: the pitch to feis committees, with pricing on the same page
// (no separate pricing page; Devin 2026-10-15). No figures bar (Devin
// 2026-10-16).

const WALKTHROUGH = "mailto:info@feis.link?subject=Feis.link%20walkthrough";
const HEADLINE = [["Run", ""], ["your", ""], ["feis", ""], ["on", ""], ["Feis.link", "g"]];

const FEATURES = [
  ["Set-up in minutes", [
    "Enter your feis details: date, location and so on. We'll take it from there.",
    "Connect your Stripe account, or create one.",
    "When your feis goes live, we announce it to thousands of Feis.link families by email, and on Instagram and Facebook.",
  ]],
  ["Entries and payments", [
    "Families register and pay online. Entry money goes straight into your feis's own Stripe account.",
    "Feis.link pays the credit card fees, so the full entry price on your syllabus lands in your account.",
    "Family maximum, early-bird and late fees, entry caps, and a private link for late or special entries.",
  ]],
  ["Before the day", [
    "Competitor lists, auto-numbered number cards, check-in and score sheets.",
    "A schedule builder with stages and time estimates.",
    "Teachers can see every registration from their school, dancer by dancer and competition by competition.",
  ]],
  ["On the day", [
    "Results show up on parents' phones the moment each competition is published.",
    "Check-in and the awards table run on any device: an iPad, a tablet or a phone.",
    "Paper is always there as a fallback, with printed check-in and score sheets.",
  ]],
  ["Results families can read", [
    "The full results grid, with every judge's placing, laid out so families can read and follow it at a glance.",
    "Teachers have full access to all of their dancers' scores, comments and the full grid.",
    "Judges' comments go privately to the dancer's family.",
  ]],
];

const STEPS = [
  ["Create your account", "Sign up as an organiser. It takes a couple of minutes."],
  ["Set up your feis", "Enter your feis details and connect or create your Stripe account. We'll take it from there."],
  ["Open registration", "We announce your feis to thousands of Feis.link families by email, and on Instagram and Facebook."],
  ["Feis day", "At your first feis we come in person, free, to train your team. After that, you run it and we're on call."],
];

const Organisers = () => {
  const root = useRef(null);
  const location = useLocation();
  const [past, setPast] = useState(null);

  usePageMeta(
    "Run Your Feis on Feis.link: Free for Organisers",
    "Online entries, payments to your own Stripe account, tabulation with Irish points and fast results. Trusted by feiseanna since 2019.",
  );

  useEffect(() => {
    let cancelled = false;
    loadListings(moment(new Date()).utc(true).format())
      .catch(() => ({}))
      .then((buckets) => { if (!cancelled) setPast(buildEventFeed(buckets, new Date()).past); });
    return () => { cancelled = true; };
  }, []);

  // Setting up is the main action (Devin 2026-10-16); the walkthrough is the
  // second. Signed in, the signup screen doesn't exist, so it's the dashboard.
  const setUp = signedIn() ? dashboardPath() : "/signup-org";
  const crests = useMemo(() => crestWall(past || []), [past]);
  const animate = useSiteMotion(root);
  useHashScroll(location, past !== null);

  return (
    <div className={`site${animate ? " js" : ""}`} ref={root}>
      <SiteHeader active="organisers" organiser />
      <main>
        <section className="hero org-hero">
          <div className="wrap">
            <span className="eyebrow rv">For organisers</span>
            <h1>
              {HEADLINE.map(([w, c], i) => (
                <React.Fragment key={w}>
                  {i > 0 && " "}
                  <span className={`w${c ? ` ${c}` : ""}`} style={{ "--i": i }}>{w}</span>
                </React.Fragment>
              ))}
              <span className="w a" style={{ "--i": HEADLINE.length }}>.</span>
            </h1>
            <p className="lede rv">Every part of feis day in one modern, carefully designed system: registration, payments, tabulation and results. Built by a feis organiser, and free for the feis.</p>
            <div className="hero-acts rv">
              <Link className="sbtn big" to={setUp}>Set up your feis</Link>
              <a className="sbtn big ghost" href={WALKTHROUGH}>Book a 20-minute walkthrough</a>
            </div>
          </div>
        </section>

        {crests.length > 0 && (
          <section className="wrap org-sec" aria-labelledby="who-h">
            <div className="sec-head rv"><div><h2 id="who-h">Feiseanna on Feis.link</h2><p>From Kelowna to Dartmouth, in their own colours.</p></div></div>
            <ul className="crest-wall">
              {crests.map((c, i) => (
                <li className="rv" style={{ "--i": i % 6 }} key={c.name}>
                  <img className="crest" src={c.logo || favIcon} alt="" loading="lazy" onError={crestFallback} />
                  {c.name}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="wrap org-sec" aria-labelledby="sys-h">
          <div className="sec-head rv"><div><h2 id="sys-h">Everything for the day, in one system</h2><p>From the syllabus to the last award.</p></div></div>
          <div className="feat-list">
            {FEATURES.map(([title, points], i) => (
              <article className="feat glow rv" key={title}>
                <span className="num">{i + 1}</span>
                <div>
                  <h3>{title}</h3>
                  <ul>{points.map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="wrap org-sec" id="pricing" aria-labelledby="price-h">
          <div className="sec-head rv"><div><h2 id="price-h">Pricing</h2><p>Simple, and the same for every feis.</p></div></div>
          <div className="price-list">
            <article className="price glow rv">
              <div className="price-big">$0</div>
              <div>
                <span className="eyebrow">For the feis</span>
                <h3>Nothing</h3>
                <p>No set-up fee, no subscription, no tabulation charge and no credit card fees. The price on your syllabus is exactly what gets deposited into your Stripe account.</p>
              </div>
            </article>
            <article className="price glow rv">
              <div className="price-big sm">$10<small>to</small>$20</div>
              <div>
                <span className="eyebrow">For families</span>
                <h3>One service fee per order</h3>
                <table className="fees">
                  <tbody>
                    <tr><td>Order up to $100</td><td>$10</td></tr>
                    <tr><td>$100 to $200</td><td>10%</td></tr>
                    <tr><td>Over $200</td><td>$20, never more</td></tr>
                  </tbody>
                </table>
              </div>
            </article>
          </div>
        </section>

        <section className="wrap org-sec" aria-labelledby="sup-h">
          <div className="sec-head rv"><div><h2 id="sup-h">You run it. We're a call away.</h2><p>Feis.link is built so your own committee runs feis day, with us on call whenever you need us.</p></div></div>
          <div className="sup-list compact">
            <article className="sup marked rv"><div><h3>Your first feis: we come to you</h3><p>We'll be there in person to set up with you and train your tabulators, stage and awards volunteers.</p></div><b className="cost"><span>No charge</span></b></article>
            <article className="sup marked rv"><div><h3>Every feis after that: on call</h3><p>Your team runs the day, with us on call remotely whenever you need us.</p></div><b className="cost"><span>No charge</span></b></article>
            <article className="sup marked rv"><div><h3>Want us on site again?</h3><p>On-site support at later feiseanna is available for a fee. Email <a className="inline" href="mailto:info@feis.link">info@feis.link</a>.</p></div><b className="cost amber"><span>On request</span></b></article>
          </div>
        </section>

        <section className="join" aria-labelledby="made-h">
          <div className="wrap">
            <div className="founder rv"><img src={founder} alt="Devin Cornell with his dancers in team costumes" loading="lazy" width="1600" height="762" /></div>
            <div className="panel made-panel rv">
              <h2 id="made-h">Built by someone who runs feiseanna</h2>
              <blockquote>“I've organised feiseanna for more than ten years, I adjudicate, and I'm a dance parent. I built Feis.link to be the system I wanted on feis day.”</blockquote>
              <p className="who"><b>Devin Cornell, ADCRG</b> · Founder · <Link className="inline" to="/about">About Feis.link</Link></p>
            </div>
          </div>
        </section>

        <section className="wrap org-sec" aria-labelledby="start-h">
          <div className="sec-head rv"><div><h2 id="start-h">How to start</h2></div></div>
          <ol className="org-steps">
            {STEPS.map(([title, text], i) => (
              <li className="rv" key={title}><span className="num">{i + 1}</span><div><h3>{title}</h3><p>{text}</p></div></li>
            ))}
          </ol>
          <div className="cta rv">
            <div>
              <h2>Set up your feis</h2>
              <p>It's free for the feis. Rather see it first? Book a 20-minute walkthrough and we'll show you how your feis would run on Feis.link. <a className="inline" href="mailto:info@feis.link">info@feis.link</a></p>
            </div>
            <div className="cta-acts">
              <Link className="sbtn big" to={setUp}>Set up your feis</Link>
              <a className="sbtn big ghost" href={WALKTHROUGH}>Book a walkthrough</a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Organisers;
